// web-runtime.ts — the SHARED piece that lets an Oriora Tauri app run as a plain web page.
//
// On desktop/iOS the real Tauri `invoke` handles everything (native SQLite, native auth
// storage, native windows). In a browser there is no Tauri: this module provides a drop-in
// `invoke` replacement that serves the app's data from the browser and handles the few
// platform commands the web way. The app's own React UI, pure helpers, and brain calls are
// unchanged — only the `invoke` boundary differs.
//
// What's SHARED (here): Tauri detection, a generic browser table store (localStorage-backed),
// the generic platform commands (auth storage, open-external, backup export/import), and the
// invoke shim that wires them together.
//
// What's PER-APP (in each app's web-commands.ts): the map from the app's data commands
// (list_x / create_x / …) to WebStore operations, plus any derived-signal command reimplemented
// in JS. Plain CRUD is a few lines each; only genuinely-computed features (recaps, signals)
// need real JS logic.
//
// Cloud sync: the WebStore persists to localStorage for offline use, and its snapshot round-trips
// through the SAME backup envelope the native side uses — so a web session reads/writes the
// customer's OWN cloud (GitHub / Google Drive) via the existing backup relay. Oriora stores nothing.

export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

// ── Generic browser-side table store ────────────────────────────────────────────
export type Row = Record<string, unknown> & { id: string };
export type Tables = Record<string, Row[]>;

// ── JSON-column revival (2026-09-24) ────────────────────────────────────────────
// Native SQLite has no array type, so a column the web store holds as a real array is TEXT
// containing JSON on the native side. A native→web restore therefore hands this store the STRING
// "[]" where it expects []. Measured on Check-in before this existed: the board rendered
// "· 2 categories" on every single-score assessment, because `App.tsx:402` reads
// `category_labels.length` and `"[]".length === 2` while `[].length === 0`. Wrong and visible.
//
// 🔴 WHITELIST, NEVER A CONTENT HEURISTIC. The columns below are derived by READING this app's own
// `web-commands.ts` create/update handlers — every column coerced to an array/object or defaulted
// to []/{} — not by sniffing values. "Revive any string that parses as JSON" was considered and
// REFUSED: it would turn a user's note reading "[1,2]" into an array. A column not listed here is
// never touched, whatever it contains.
//
// This app has NO array/object columns. Tables read (2026-09-24): decks, cards, review_log — all
// scalars; db.rs uses no serde_json. The `Array.isArray(cards)` in web-commands.ts is a bulk-add
// ARGUMENT, not a column.
const JSON_COLUMNS: Record<string, string[]> = {};

/// Revive whitelisted JSON-in-TEXT columns in place. Never throws: a value that is not a string,
/// or that fails to parse, or that parses to something that is not an array/object, is left
/// exactly as it was. Harmless on a web-made file, where these are already arrays.
function reviveJsonColumns(t: Tables): Tables {
  for (const [table, cols] of Object.entries(JSON_COLUMNS)) {
    for (const row of t[table] ?? []) {
      for (const c of cols) {
        const v = row[c];
        if (typeof v !== "string") continue;
        try {
          const parsed = JSON.parse(v);
          if (parsed !== null && typeof parsed === "object") row[c] = parsed;
        } catch {
          /* not JSON — a genuine string value; leave it alone */
        }
      }
    }
  }
  return t;
}

/// Both backup value shapes in, `Tables` out; null for anything that is neither.
//  web   : {table: [{col: val}, …]}                     — already Tables
//  native: {"tables": {table: {columns: [], rows: [[]]}}} — columnar, plus a "tables" wrapper
//  The two differ in TWO ways (the wrapper AND the row representation), which is why a plain
//  key-alias was never enough. A missing cell becomes null — `?? null` only fills null/undefined,
//  so a legitimate 0 or "" survives untouched.
export function toTables(raw: Record<string, unknown>): Tables | null {
  if (Object.values(raw).every((v) => Array.isArray(v))) return raw as Tables;
  const inner = (raw as { tables?: unknown }).tables;
  const src =
    inner && typeof inner === "object" && !Array.isArray(inner) ? (inner as Record<string, unknown>) : raw;
  const out: Tables = {};
  for (const [table, v] of Object.entries(src)) {
    if (!v || typeof v !== "object" || Array.isArray(v)) return null;
    const td = v as { columns?: unknown; rows?: unknown };
    if (!Array.isArray(td.columns) || !Array.isArray(td.rows)) return null;
    const cols = td.columns as string[];
    // 🔴 A ROW MUST MATCH ITS COLUMN LIST (2026-09-25). This used to pad a short row with `?? null`,
    // which meant a malformed columnar file imported "successfully" and left null where the UI
    // expects a value — `record.field.length` then throws exactly like the web-shim array-default
    // crash (reproduced: `null.length` → "Cannot read properties of null"), blanking the screen with
    // the data already replaced. Before 2026-09-24 such a file was refused outright; the layer-3
    // reader widened the door, so the check that used to be implicit is made explicit here. A
    // well-formed `export_envelope` always writes every column on every row, so this rejects only
    // genuinely corrupt input — and it rejects it LOUDLY, on the existing "unreadable" path, instead
    // of half-importing it.
    const rows = td.rows as unknown[][];
    if (rows.some((r) => !Array.isArray(r) || r.length !== cols.length)) return null;
    out[table] = rows.map((r) => Object.fromEntries(cols.map((c, i) => [c, r[i]])) as Row);
  }
  return out;
}

export class WebStore {
  private storageKey: string;
  private tables: Tables;

  constructor(appSlug: string, seed: Tables = {}) {
    this.storageKey = `oriora.webstore.${appSlug}`;
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem(this.storageKey) : null;
    this.tables = saved ? (JSON.parse(saved) as Tables) : seed;
  }

  private persist(): void {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(this.storageKey, JSON.stringify(this.tables));
    }
  }

  all(table: string): Row[] {
    return this.tables[table] ? this.tables[table].map((r) => ({ ...r })) : [];
  }
  get(table: string, id: string): Row | null {
    return (this.tables[table] ?? []).find((r) => r.id === id) ?? null;
  }
  insert(table: string, row: Row): Row {
    (this.tables[table] ??= []).push(row);
    this.persist();
    return { ...row };
  }
  update(table: string, id: string, patch: Partial<Row>): void {
    const arr = this.tables[table] ?? [];
    const i = arr.findIndex((r) => r.id === id);
    if (i >= 0) {
      arr[i] = { ...arr[i], ...patch };
      this.persist();
    }
  }
  remove(table: string, id: string): void {
    this.tables[table] = (this.tables[table] ?? []).filter((r) => r.id !== id);
    this.persist();
  }

  /** Replace the whole collection (used by import_backup / cloud restore). */
  replaceAll(tables: Tables): void {
    this.tables = tables;
    this.persist();
  }
  /** Deep copy of all tables (used by export_backup / cloud save). */
  snapshot(): Tables {
    return JSON.parse(JSON.stringify(this.tables)) as Tables;
  }
}

// ── The web invoke shim ─────────────────────────────────────────────────────────
export type CommandHandler = (args: Record<string, unknown>) => unknown | Promise<unknown>;

/**
 * Build a browser `invoke(cmd, args)` that mirrors the app's Tauri command surface.
 * `dataCommands` = the app's data operations (per-app, in web-commands.ts).
 * The generic platform commands (auth storage, open-external, backup, ping) are provided here.
 * `store` is the app's WebStore, used for the generic backup export/import commands.
 */
export function makeWebInvoke(
  appSlug: string,
  store: WebStore,
  dataCommands: Record<string, CommandHandler>,
) {
  const generic: Record<string, CommandHandler> = {
    // Supabase session persistence → localStorage (native uses the Tauri fs).
    auth_storage_get: ({ key }) => localStorage.getItem(`oriora.auth.${appSlug}.${String(key)}`),
    auth_storage_set: ({ key, value }) => {
      localStorage.setItem(`oriora.auth.${appSlug}.${String(key)}`, String(value));
      return null;
    },
    auth_storage_remove: ({ key }) => {
      localStorage.removeItem(`oriora.auth.${appSlug}.${String(key)}`);
      return null;
    },
    // Open links in a new tab (native opens the system browser).
    open_external: ({ url }) => {
      window.open(String(url), "_blank", "noopener,noreferrer");
      return null;
    },
    // On web the login page is a plain redirect (see App.tsx web branch); nothing to open in-app.
    open_login_window: () => null,
    ping: () => "pong",
    // Data-portability round-trips through the SAME envelope shape as the native side.
    export_backup: () =>
      JSON.stringify(
        { oriora_backup: { app: appSlug, schema: 1, exported_at: new Date().toISOString(), data: store.snapshot() } },
        null,
        2,
      ),
    import_backup: ({ json }) => {
      const parsed = JSON.parse(String(json)) as { oriora_backup?: { app?: string; data?: unknown; dump?: unknown } };
      const envelope = parsed?.oriora_backup;
      if (!envelope) throw new Error("Not a valid Oriora backup file.");
      // Mirror backup.rs's app-identity check, which this path skipped entirely — a web session
      // would silently import ANY app's backup. Compared with `-`/`_` folded (same reasoning as
      // the Rust side): the web build's own export writes appSlug's hyphen form, native writes
      // its underscore form, and a customer's own backup from either surface must still count as
      // a match here. Rolled fleet-wide 2026-09-17 (Oriora Check-in, iOS Simulator).
      const norm = (s: string) => s.replace(/-/g, "_");
      const a = envelope.app ?? "";
      if (norm(a) !== norm(appSlug)) throw new Error(`This backup is from a different app (${a || "unknown"}).`);
      // Accept either KEY without changing what either surface writes — native writes "dump",
      // this file's own export above writes "data" for the same concept. This does NOT make the
      // two VALUE SHAPES interchangeable: native's "dump" is columnar ({columns, rows} per
      // table), not this file's row-object-array Tables shape, so a real "dump" payload is
      // rejected explicitly below rather than silently corrupting localStorage.
      const raw = envelope.data ?? envelope.dump;
      if (!raw || typeof raw !== "object") throw new Error("Not a valid Oriora backup file.");
      // LAYER 3 (2026-09-24): accept BOTH value shapes, convert on read, change no writer.
      // This used to reject a native payload outright — layers 1–2 (identifier fold, either key,
      // 2026-09-17) got a native file as far as here and it died on "unreadable". Customers hold
      // files in both shapes, so the reader widens and the writer above stays exactly as it is.
      const converted = toTables(raw as Record<string, unknown>);
      if (!converted) throw new Error("This backup's data is unreadable.");
      // Applied to BOTH envelope shapes: a web-made file already holds arrays, so this is a no-op
      // there, and running it unconditionally means there is no shape-dependent branch to drift.
      const data = reviveJsonColumns(converted);
      store.replaceAll(data);
      const tables = Object.keys(data).length;
      const rows = Object.values(data).reduce((n, arr) => n + arr.length, 0);
      return { tables, rows };
    },
  };

  const all: Record<string, CommandHandler> = { ...generic, ...dataCommands };

  return async function webInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
    const handler = all[cmd];
    if (!handler) throw new Error(`web-runtime: no handler for command '${cmd}' (add it to web-commands.ts)`);
    return (await handler(args ?? {})) as T;
  };
}
